import React, { useState, useEffect } from 'react';
import { Button, Modal, message, Tooltip, Select, Progress } from 'antd';
import { Form, InputGroup, Badge } from 'react-bootstrap';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    HeartOutlined,
    AlertOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { oneKlicks } from '../../SDK/oneclick';
import { getuserdata } from '../../services/apiService';
import Paginationnew from '../../Uitiles/Paginationnew';

const BloodGroups = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // Modal states
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);

    // Form data
    const [formData, setFormData] = useState({
        group_name: '',
        quantity: 0,
        unit: 'units'
    });

    const bloodGroupOptions = [
        { value: 'A+', label: 'A+ (A Positive)' },
        { value: 'A-', label: 'A- (A Negative)' },
        { value: 'B+', label: 'B+ (B Positive)' },
        { value: 'B-', label: 'B- (B Negative)' },
        { value: 'AB+', label: 'AB+ (AB Positive)' },
        { value: 'AB-', label: 'AB- (AB Negative)' },
        { value: 'O+', label: 'O+ (O Positive)' },
        { value: 'O-', label: 'O- (O Negative)' },
    ];

    const unitOptions = [
        { value: 'units', label: 'Units' },
        { value: 'bags', label: 'Bags' },
        { value: 'ml', label: 'Milliliters (ml)' },
    ];

    const bloodGroupColors = {
        'A+': 'danger', 'A-': 'danger',
        'B+': 'primary', 'B-': 'primary',
        'AB+': 'secondary', 'AB-': 'secondary',
        'O+': 'success', 'O-': 'success'
    };

    useEffect(() => {
        initializeData();
    }, [page, searchText]);

    const initializeData = async () => {
        const id = await getuserdata('id');
        setUserId(id);
        getBloodGroups(id);
    };

    const getBloodGroups = async (id) => {
        try {
            setLoading(true);
            const currentUserId = id || userId;
            const conditions = { blood_bank_id: currentUserId };
            const search = searchText ? {
                group_name: searchText,
            } : {};
            const pagination = { page, limit };

            const response = await oneKlicks.read('', 'blood_groups', conditions, 'group_name ASC', search, pagination);

            if (response && response.data) {
                setData(response.data);
                setTotalPages(response.totalPages || 1);
            }
        } catch (error) {
            console.error('getBloodGroups error:', error);
            message.error('Failed to fetch blood groups');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setFormData({ group_name: '', quantity: 0, unit: 'units' });
        setAddModalVisible(true);
    };

    const handleAddSave = async () => {
        if (!formData.group_name) {
            message.error('Please select a blood group');
            return;
        }

        // Check if group already exists (client-side check for now, can be server-side)
        // Since we are paging, this might not catch duplicates on other pages, but usually there are only 8 groups.
        // Better to rely on unique constraint or server check, but here's a simple check if loaded:
        const exists = data.find(item => item.group_name === formData.group_name);
        if (exists) {
            message.error('This blood group already exists. Please edit the existing entry.');
            return;
        }

        try {
            setLoading(true);
            const payload = {
                blood_bank_id: userId,
                group_name: formData.group_name,
                quantity: formData.quantity || 0,
                unit: formData.unit || 'units',
                last_updated: new Date().toISOString().slice(0, 19).replace('T', ' ')
            };

            const response = await oneKlicks.create('', 'blood_groups', payload);
            if (response.success) {
                message.success('Blood group added successfully');
                setAddModalVisible(false);
                getBloodGroups(userId);
            } else {
                message.error(response.message || 'Failed to add');
            }
        } catch (error) {
            console.error('Add error:', error);
            message.error('Failed to add blood group');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (record) => {
        setEditingGroup(record);
        setFormData({
            group_name: record.group_name,
            quantity: record.quantity || 0,
            unit: record.unit || 'units'
        });
        setEditModalVisible(true);
    };

    const handleEditSave = async () => {
        try {
            setLoading(true);
            const payload = {
                quantity: formData.quantity || 0,
                unit: formData.unit || 'units',
                last_updated: new Date().toISOString().slice(0, 19).replace('T', ' ')
            };

            const response = await oneKlicks.update('', 'blood_groups', payload, { id: editingGroup.id });
            if (response.success) {
                message.success('Blood group updated successfully');
                setEditModalVisible(false);
                getBloodGroups(userId);
            } else {
                message.error(response.message || 'Failed to update');
            }
        } catch (error) {
            console.error('Update error:', error);
            message.error('Failed to update blood group');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (record) => {
        if (window.confirm(`Are you sure you want to delete blood group "${record.group_name}"?`)) {
            try {
                const response = await oneKlicks.delete('', 'blood_groups', { id: record.id });
                if (response && response.success) {
                    message.success('Blood group deleted successfully');
                    getBloodGroups(userId);
                }
            } catch (error) {
                message.error('Failed to delete blood group');
            }
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    return (
        <div className="container-fluid py-3">
            <div className="card mb-5">
                <div className="card-body">
                    <div className="align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <h5 className="mb-0 font-weight-bold mr-3">
                                <HeartOutlined className="me-2 text-danger" />
                                Blood Groups
                            </h5>
                            <Badge bg="danger">Inventory</Badge>
                        </div>
                        <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                            <InputGroup className="flex-grow-1 me-2 h-40" style={{ maxWidth: '300px' }}>
                                <Form.Control
                                    placeholder="Search blood group..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </InputGroup>
                            <Button className="btn-primary h-40" onClick={handleAdd}>
                                <PlusOutlined /> Add Blood Group
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0 align-middle">
                            <thead>
                                <tr>
                                    <th>Group Name</th>
                                    <th>Quantity</th>
                                    <th>Stock Status</th>
                                    <th>Last Updated</th>
                                    <th style={{ width: '150px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4">Loading...</td>
                                    </tr>
                                ) : data.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4">No blood groups found</td>
                                    </tr>
                                ) : (
                                    data.map((item) => (
                                        <tr key={item.id}>
                                            <td data-label="Group Name">
                                                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                                    <Badge bg={bloodGroupColors[item.group_name] || 'secondary'}>
                                                        {item.group_name}
                                                    </Badge>
                                                </div>
                                            </td>
                                            <td data-label="Quantity">
                                                <div className="d-flex align-items-center">
                                                    <span style={{ fontSize: '18px', fontWeight: 'bold', marginRight: '8px' }}>
                                                        {item.quantity}
                                                    </span>
                                                    <span className="text-muted small">{item.unit || 'units'}</span>
                                                </div>
                                            </td>
                                            <td data-label="Stock Status">
                                                <div style={{ width: '150px' }}>
                                                    <Progress
                                                        percent={Math.min((parseInt(item.quantity) / 50) * 100, 100)}
                                                        strokeColor={parseInt(item.quantity) < 5 ? '#ff4d4f' : '#52c41a'}
                                                        showInfo={false}
                                                        size="small"
                                                    />
                                                    {(parseInt(item.quantity) || 0) < 5 && (
                                                        <small className="text-danger">
                                                            <AlertOutlined className="me-1" /> Low Stock
                                                        </small>
                                                    )}
                                                    {(parseInt(item.quantity) || 0) >= 5 && (
                                                        <small className="text-success">In Stock</small>
                                                    )}
                                                </div>
                                            </td>
                                            <td data-label="Last Updated">
                                                {item.last_updated ? new Date(item.last_updated).toLocaleString() : '-'}
                                            </td>
                                            <td data-label="Actions">
                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                    <Button
                                                        className="btn-info"
                                                        onClick={() => handleEdit(item)}
                                                        style={{ fontSize: '12px', padding: '4px 8px' }}
                                                    >
                                                        <EditOutlined />
                                                    </Button>
                                                    <Button
                                                        className="btn-danger"
                                                        onClick={() => handleDelete(item)}
                                                        style={{ fontSize: '12px', padding: '4px 8px' }}
                                                    >
                                                        <DeleteOutlined />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Paginationnew currentPage={page} totalPages={totalPages} handlePageChange={handlePageChange} />
                </div>
            </div>

            {/* Add Modal */}
            <Modal
                title="Add Blood Group"
                open={addModalVisible}
                onCancel={() => setAddModalVisible(false)}
                onOk={handleAddSave}
                confirmLoading={loading}
                width={500}
            >
                <div className="row">
                    <div className="col-12 mb-3">
                        <label className="form-label">Blood Group *</label>
                        <Select
                            className="w-100"
                            placeholder="Select blood group"
                            options={bloodGroupOptions}
                            value={formData.group_name || undefined}
                            onChange={(val) => setFormData({ ...formData, group_name: val })}
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Quantity</label>
                        <Form.Control
                            type="number"
                            min="0"
                            value={formData.quantity}
                            onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Unit</label>
                        <Select
                            className="w-100"
                            options={unitOptions}
                            value={formData.unit}
                            onChange={(val) => setFormData({ ...formData, unit: val })}
                        />
                    </div>
                </div>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title={`Edit ${editingGroup?.group_name || 'Blood Group'}`}
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={handleEditSave}
                confirmLoading={loading}
                width={500}
            >
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Quantity</label>
                        <Form.Control
                            type="number"
                            min="0"
                            value={formData.quantity}
                            onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Unit</label>
                        <Select
                            className="w-100"
                            options={unitOptions}
                            value={formData.unit}
                            onChange={(val) => setFormData({ ...formData, unit: val })}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default BloodGroups;
